/**
 * EntityRef — read-only friendly label for an FK column value.
 *
 * Used in list-page tables where a row holds e.g. `agreement_id` as a UUID
 * but we want to show the agreement's `code_1`. Fetches the entity once
 * (cached in react-query for 30s) and renders the friendly label, falling
 * back to a short UUID prefix while loading or on error.
 */
import { Anchor, Text, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { accountInsuranceApi } from '../api/accountInsurance';
import { accountsApi } from '../api/accounts';
import { agreementsApi } from '../api/agreements';
import { personsApi } from '../api/persons';
import type { UUID } from '../api/types';

interface RefProps {
  /** Nullable for Indirect Accounts whose direct agreement_id is null
   *  (migration 0028); also nullable on other recently-relaxed FKs. The
   *  components below render an em-dash when id is null/undefined so the
   *  list pages don't crash while #270-#273 sync the frontend types. */
  id: UUID | null | undefined;
  /** When set, the label is wrapped in a router Link to the detail page. */
  linkPathPrefix?: string;
}

/** Renders a short id prefix, or an em-dash if id is null/undefined.
 *  Defensive fix 2026-06-08: previously crashed with
 *  "Cannot read properties of null (reading 'slice')" when callers
 *  passed a nullable FK (e.g. Account.agreement_id on Indirect Accounts).
 *  Tracked under #270-#273 for proper handling in the new schema. */
function ShortId({ id }: { id: UUID | null | undefined }) {
  if (!id) return <Text size="xs" c="dimmed">—</Text>;
  return <Text size="xs" ff="monospace" c="dimmed">{id.slice(0, 8)}…</Text>;
}

/** Shown when the referenced entity 404s (typically soft-deleted): a muted
 *  "(deleted)" with the id in a tooltip, instead of a bare UUID stub. Paired
 *  with retry:0 below so a genuinely-missing ref no longer double-fetches (#1). */
function MissingRef({ id }: { id: UUID }) {
  return (
    <Tooltip label={`Reference not found — likely deleted (${id.slice(0, 8)}…)`} withArrow>
      <Text size="xs" c="dimmed" fs="italic">(deleted)</Text>
    </Tooltip>
  );
}

export function AgreementRef({ id, linkPathPrefix }: RefProps) {
  const q = useQuery({
    queryKey: ['ref', 'agreement', id],
    queryFn: () => agreementsApi.get(id!),
    staleTime: 30_000,
    retry: 0,
    enabled: Boolean(id),
  });
  if (!id) return <Text size="xs" c="dimmed">—</Text>;
  if (q.isError) return <MissingRef id={id} />;
  if (q.isLoading || !q.data) return <ShortId id={id} />;
  const label = `${q.data.code_1} — ${q.data.client_mnemonic}`;
  return linkPathPrefix
    ? <Anchor component={Link} to={`${linkPathPrefix}/${id}`} size="sm">{label}</Anchor>
    : <Text size="sm">{label}</Text>;
}

export function PersonRef({ id, linkPathPrefix }: RefProps) {
  const q = useQuery({
    queryKey: ['ref', 'person', id],
    queryFn: () => personsApi.get(id!),
    staleTime: 30_000,
    retry: 0,
    enabled: Boolean(id),
  });
  if (!id) return <Text size="xs" c="dimmed">—</Text>;
  if (q.isError) return <MissingRef id={id} />;
  if (q.isLoading || !q.data) return <ShortId id={id} />;
  const label = q.data.display_name;
  return linkPathPrefix
    ? <Anchor component={Link} to={`${linkPathPrefix}/${id}`} size="sm">{label}</Anchor>
    : <Text size="sm">{label}</Text>;
}

/**
 * Phase A3.e (2026-06-09): one-line label for an Insurance Policy reference.
 * Renders "Insurer — Policy#" with optional Link wrap. Used on the Accounts
 * list page's new Policy column to label Indirect accounts' parent Policy.
 */
export function InsurancePolicyRef({ id, linkPathPrefix }: RefProps) {
  const q = useQuery({
    queryKey: ['ref', 'account-insurance', id],
    queryFn: () => accountInsuranceApi.get(id!),
    staleTime: 30_000,
    retry: 0,
    enabled: Boolean(id),
  });
  if (!id) return <Text size="xs" c="dimmed">—</Text>;
  if (q.isError) return <MissingRef id={id} />;
  if (q.isLoading || !q.data) return <ShortId id={id} />;
  const label = `${q.data.insurer} — ${q.data.policy_number}`;
  return linkPathPrefix
    ? <Anchor component={Link} to={`${linkPathPrefix}/${id}`} size="sm">{label}</Anchor>
    : <Text size="sm">{label}</Text>;
}

export function AccountRef({ id, linkPathPrefix }: RefProps) {
  const q = useQuery({
    queryKey: ['ref', 'account', id],
    queryFn: () => accountsApi.get(id!),
    staleTime: 30_000,
    retry: 0,
    enabled: Boolean(id),
  });
  if (!id) return <Text size="xs" c="dimmed">—</Text>;
  if (q.isError) return <MissingRef id={id} />;
  if (q.isLoading || !q.data) return <ShortId id={id} />;
  // internal_num nullable since migration 0019; render bank-only when null.
  const label = q.data.internal_num
    ? `${q.data.bank_name} — ${q.data.internal_num}`
    : q.data.bank_name;
  return linkPathPrefix
    ? <Anchor component={Link} to={`${linkPathPrefix}/${id}`} size="sm">{label}</Anchor>
    : <Text size="sm">{label}</Text>;
}
